/**
 * SinglePageForm.tsx — the clinician's ONE-PAGE questionnaire (2026-07-21,
 * PR 4/5). "For a clinician it doesn't need to be step by step — the whole
 * form on one page" (CTO): a clinician on a live call fills fields in
 * whatever order the conversation goes.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * HOW it stays honest with the SERVER-GATED engine (the same one the patient
 * journey drives one screen at a time):
 *   - Every field posts its answer INDIVIDUALLY (POST …/answers) through a
 *     SEQUENTIAL queue — the engine evaluates per answer, so order is
 *     preserved and two edits can never race. Each field shows its own
 *     Saving…/Saved chip; a FAILED save shows a Retry button that re-queues
 *     the SAME value (a radio can't be "changed to retry"), the key stops
 *     counting toward completeness, and Submit stays disabled until every
 *     failed answer lands — the server never receives an incomplete submit.
 *     The one field that does NOT post per change is the DATE — a typeable
 *     date field emits every half-typed prefix, and only a whole one is an
 *     answer; see {@link DateField}.
 *   - Leading `capture`-kind questions auto-submit from `capturePrefill`
 *     exactly like QuestionFlow (they set the BMI gate); the capture also
 *     renders as an editable height/weight row for mid-call corrections.
 *   - UX Wave 9 §2 — HEIGHT/WEIGHT IS CAPTURED ONCE: only the FIRST
 *     capture-kind question renders; the duplicate "confirm your height and
 *     weight" capture(s) are SUPPRESSED from the page and auto-answered from
 *     the primary capture's values through the NORMAL answer POST (the
 *     server-gated engine still requires an answer row per capture — the
 *     wire stays satisfied, the clinician types the numbers once). Covered
 *     on all three paths: the fresh-start auto-submit, a resumed form whose
 *     confirm was never answered (gap-fill on load), and a mid-call edit of
 *     the rendered row (the save bundles the confirm re-posts; a failed
 *     bundle member flags the rendered row and blocks submit as usual).
 *   - UX Wave 9 §2 — once height/weight are present, the patient journey's
 *     weight-reduction PROJECTION chart renders ALONGSIDE the form (the
 *     same src/journey component — R28-safe, category-level, no product or
 *     price), and the BMI dial appears once height/weight + sex + date of
 *     birth are all answered (client compute mirroring the journey maths;
 *     the server's BMI stays authoritative).
 *   - R28: restricted questions arrive ONLY once the server gate passes — a
 *     save whose `nextQuestionKey` names an unseen key triggers a question
 *     REFETCH and the new fields merge into the page.
 *   - MEDICATION-SAFETY PROMPT (2026-08-19): a live fill-on-behalf form heads
 *     with the standing reminder that the STAFF MEMBER has to explain the
 *     treatment's risks on this call. The patient never meets the checkout
 *     safety gate on this lane — someone filled the form for them — so the
 *     person on the phone is the only safety net there is.
 *   - soft_stop → the "a clinician will review" note; keep going.
 *   - hard_stop → the WHOLE form greys out and a popup says the person is
 *     not eligible (with the server's signposting lines — R31 rides them for
 *     mh risk). The deal auto-moves to Lost server-side (`onStopped` lets the
 *     drawer refresh). No further answers post.
 *   - Submit — the CTA reads "CHECK ELIGIBILITY" (UX Wave 9 §3: the verdict
 *     is the point of the submit) and POSTs the AUTHED staff submit
 *     (…/submit/staff, Wave 9 §4 — the verified CLINICIAN role lets the
 *     server write the assessment_submitted audit row in the submit tx);
 *     `not_eligible` renders the same greyed + popup treatment (CLIN-02:
 *     never a positive verdict); otherwise the verdict goes up via
 *     `onSubmitted` (the drawer leads with the eligibility RESULT).
 *   - `readOnly` (ADMIN viewers, or an already-submitted form): values render
 *     from the server's raw answers, every control disabled, nothing posts.
 *   - `alreadyNotEligible` (CLIN-03 sticky stop on resume): the form mounts
 *     already frozen with the not-eligible banner — the modal is NOT re-opened
 *     (that popup is only for the live hard-stop / submit moment; remounting
 *     the Questionnaire tab must not keep throwing the dialog).
 *   - A PROGRESS strip heads the page (2026-07-21 UX pass): a brand-green
 *     ProgressRail + "N of M answered" chip over the currently-VISIBLE
 *     answerable questions, updating live as answers save (failed saves do
 *     not count — same honesty rule as the Submit gate).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { submitStaffAnswer, submitStaffAssessment } from '../api/clinic';
import { getQuestions, grantPhotoUpload, uploadPhoto } from '../api/journey';
import type { AnswerValue, AssessmentSubmitResult, EvaluationResult, QuestionPublic, StaffAnswerResult } from '../api/types';
import { BmiDial } from '../journey/BmiDial';
import { ProjectionChart } from '../journey/ProjectionChart';
import { bmiOf } from '../journey/projection';
import { DatePicker, todayIso } from '../ui/DatePicker';
import { DialogShell } from '../ui/ConfirmDialog';
import { Alert, Badge, Button, CheckboxRow, ErrorState, Field, Help, ProgressRail, TextArea, TextInput , CheckIcon} from '../ui/kit';
import { DrawerSkeleton } from '../ui/skeletons';

type AnswerMap = Record<string, AnswerValue>;
type SaveState = 'saving' | 'saved' | 'error';

function isVisible(question: QuestionPublic, answers: AnswerMap): boolean {
  if (!question.visibleIf) return true;
  return answers[question.visibleIf.questionKey] === question.visibleIf.equals;
}

function ordered(questions: QuestionPublic[]): QuestionPublic[] {
  return [...questions].sort((a, b) => a.idx - b.idx);
}

/** Narrow an answer value to the capture {heightCm, weightKg} shape (else null). */
function captureOf(value: unknown): { heightCm: number; weightKg: number } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { heightCm, weightKg } = value as { heightCm?: unknown; weightKg?: unknown };
  if (typeof heightCm !== 'number' || typeof weightKg !== 'number') return null;
  return heightCm > 0 && weightKg > 0 ? { heightCm, weightKg } : null;
}

const chipStyle = (tone: 'muted' | 'danger') =>
  ({
    fontSize: '12px',
    fontWeight: 600,
    color: tone === 'danger' ? 'var(--gps-danger-text)' : 'var(--gps-muted)',
    marginLeft: '8px',
  }) as const;

export function SinglePageForm({
  assessmentId,
  clinicSlug,
  capturePrefill,
  prefill = {},
  prefillDetails = {},
  readOnly = false,
  alreadyNotEligible = false,
  renderQuestionExtra,
  onSubmitted,
  onStopped,
}: {
  assessmentId: string;
  /** Guest photo-upload grant still needs `?clinicSlug=` (no `/staff` twin). */
  clinicSlug: string;
  /** Height/weight for the leading capture auto-submit (the Start panel's values). Omit when resuming — the server already holds them. */
  capturePrefill?: { heightCm: number; weightKg: number };
  /** Latest raw answer values keyed by questionKey (GET …/answers) — "clinicians see previously-filled forms". */
  prefill?: Record<string, unknown>;
  /** Free-text details keyed by questionKey (the yes_no_text detail from the same read) — a resumed "yes" must show its clinical text, never wipe it. */
  prefillDetails?: Record<string, string>;
  /** ADMIN viewers / submitted forms: render values, post nothing. */
  readOnly?: boolean;
  /**
   * CLIN-03 — the assessment is already stamped not_eligible (hard stop or
   * submit). Mount frozen with the banner; do NOT auto-open the modal (the
   * live hard-stop / submit path owns that one-shot popup).
   */
  alreadyNotEligible?: boolean;
  /**
   * Wave 4 notes-per-answer: an optional per-question affordance rendered
   * under each answer (the authorise view passes the anchored-note control —
   * it needs the key for the anchor and the prompt for accessible labels).
   * The form stays notes-agnostic — it only slots the caller's node in.
   */
  renderQuestionExtra?: (question: QuestionPublic) => ReactNode;
  onSubmitted: (result: AssessmentSubmitResult) => void;
  /** A hard stop or not_eligible submit landed — the deal moved to Lost server-side. */
  onStopped?: (result: EvaluationResult | null) => void;
}) {
  const [questions, setQuestions] = useState<QuestionPublic[]>([]);
  const [answers, setAnswers] = useState<AnswerMap>(prefill as AnswerMap);
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({});
  const [softKeys, setSoftKeys] = useState<string[]>([]);
  const [stop, setStop] = useState<EvaluationResult | null>(null);
  const [stopPopup, setStopPopup] = useState(false);
  // CONFIRM-BEFORE-LOST (2026-08-06): a staff hard-stop answer the server
  // PREVIEWED (wouldCloseLost) but did NOT commit — the lead is NOT lost yet.
  // The clinician either Continues (resubmit with confirmClose) or Goes back
  // and revises the answer; the form stays editable meanwhile.
  const [pendingClose, setPendingClose] = useState<{
    question: QuestionPublic;
    value: AnswerValue;
    detail?: string;
  } | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [notEligible, setNotEligible] = useState(alreadyNotEligible);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Form-level save failure (TA-06) — ErrorState with Try again. */
  const [saveErrorKey, setSaveErrorKey] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');

  // Sticky stop can arrive via a soft detail refresh after the form mounted
  // editable — flip local freeze without re-opening the one-shot modal.
  useEffect(() => {
    if (alreadyNotEligible) setNotEligible(true);
  }, [alreadyNotEligible]);

  // The per-answer engine is order-sensitive — every save chains here.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const loadInFlightRef = useRef(false);
  // WHY: a FAILED save means the SERVER never recorded the answer even though
  // the optimistic value still shows locally — the Retry button re-sends this
  // exact payload (a radio can't be re-picked to the same value), and the
  // failed-key set gates handleSubmit after the queue flush so a failure that
  // lands mid-flush can never slip an incomplete assessment through.
  const lastAttemptRef = useRef<Record<string, { value: AnswerValue; detail?: string }>>({});
  const failedKeysRef = useRef<Set<string>>(new Set());

  const refetch = useCallback(async (): Promise<QuestionPublic[]> => {
    const set = await getQuestions(assessmentId);
    setQuestions(set.questions);
    return set.questions;
  }, [assessmentId]);

  const load = useCallback(() => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    setLoadState('loading');
    (async () => {
      const qs = ordered(await refetch());
      if (readOnly) return;
      // Auto-submit the captures — the BMI gate is evaluated exactly as the
      // step-by-step flow would. Skip any capture the server already holds
      // (prefill) — resuming must not re-post stale values. Wave 9 §2: the
      // value comes from the Start panel (fresh form) OR, on a resumed form,
      // from the PRIMARY capture's stored answer — that second path gap-fills
      // the SUPPRESSED confirm capture(s) so the engine stays satisfied even
      // though the duplicate question never renders.
      const captures = qs.filter((q) => q.kind === 'capture');
      const startValue =
        capturePrefill && capturePrefill.heightCm > 0 && capturePrefill.weightKg > 0
          ? { heightCm: capturePrefill.heightCm, weightKg: capturePrefill.weightKg }
          : null;
      const value = startValue ?? captureOf(captures[0] ? prefill[captures[0].key] : undefined);
      if (!value) return;
      for (const question of captures) {
        if (prefill[question.key] !== undefined) continue;
        const result = await submitStaffAnswer(assessmentId, { questionKey: question.key, value });
        setAnswers((prev) => ({ ...prev, [question.key]: value }));
        // Confirm-before-lost also guards a capture that would hard-stop (e.g. a
        // BMI floor from a mistyped weight) — preview it, never silently lose.
        if (result.wouldCloseLost) {
          setPendingClose({ question, value });
          return;
        }
        if (result.outcome === 'hard_stop') {
          setStop(result);
          setStopPopup(true);
          onStopped?.(result);
          return;
        }
        if (result.outcome === 'soft_stop') setSoftKeys((prev) => [...prev, question.key]);
      }
    })()
      .then(() => setLoadState('ready'))
      .catch(() => setLoadState('error'))
      .finally(() => {
        loadInFlightRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefill/capturePrefill are mount-time inputs
  }, [refetch, assessmentId, readOnly]);

  useEffect(() => {
    load();
  }, [load]);

  // The confirm-before-lost dialog is modal: while it is open the form is
  // frozen, but dismissing it ("Go back") un-freezes so the answer can be
  // revised — the lead is not lost until the clinician confirms.
  const frozen = readOnly || stop !== null || notEligible || submitting || pendingClose !== null;

  /**
   * Apply one staff answer RESULT: hard stops now go through confirm-before-lost.
   * A `wouldCloseLost` preview raises the confirm dialog (the lead is NOT lost —
   * the answer is recorded and still editable); an actual `hard_stop` (only ever
   * returned AFTER the clinician confirmed with confirmClose) freezes + closes.
   * Returns true when it consumed the result terminally (stop the bundle loop).
   */
  function applyStaffResult(
    question: QuestionPublic,
    value: AnswerValue,
    detail: string | undefined,
    result: StaffAnswerResult,
  ): boolean {
    if (result.wouldCloseLost) {
      setPendingClose({ question, value, ...(detail !== undefined ? { detail } : {}) });
      return true;
    }
    if (result.outcome === 'hard_stop') {
      setStop(result);
      setStopPopup(true);
      onStopped?.(result);
      return true;
    }
    if (result.outcome === 'soft_stop') {
      setSoftKeys((prev) => (prev.includes(question.key) ? prev : [...prev, question.key]));
    }
    return false;
  }

  /** Queue one answer; the result drives soft/hard handling + gate refetches. */
  function save(question: QuestionPublic, value: AnswerValue, detail?: string, confirmClose = false) {
    // `confirmClose` re-posts a previewed hard-stop answer to COMMIT the close;
    // it must run even though the pending-close dialog has frozen the form.
    if (frozen && !confirmClose) return;
    lastAttemptRef.current[question.key] = { value, ...(detail !== undefined ? { detail } : {}) };
    // Wave 9 §2 — a capture save is a BUNDLE: the rendered (first) capture
    // PLUS every suppressed confirm capture, all posted with the same value
    // through the same sequential queue. ONE chip/Retry governs the bundle —
    // any failed member flags the rendered row and blocks submit exactly like
    // a plain failed save would.
    const bundle =
      question.kind === 'capture'
        ? ordered(questions).filter((q) => q.kind === 'capture')
        : [question];
    setAnswers((prev) => {
      const next = { ...prev };
      for (const q of bundle) next[q.key] = value;
      return next;
    });
    setSaveStates((prev) => ({ ...prev, [question.key]: 'saving' }));
    setSaveErrorKey(null);
    queueRef.current = queueRef.current
      .then(async () => {
        for (const q of bundle) {
          const result = await submitStaffAnswer(assessmentId, {
            questionKey: q.key,
            value,
            ...(detail ? { detail } : {}),
            ...(confirmClose ? { confirmClose: true } : {}),
          });
          failedKeysRef.current.delete(question.key);
          setSaveStates((prev) => ({ ...prev, [question.key]: 'saved' }));
          // Confirm-before-lost: a would-close preview raises the dialog (lead
          // NOT lost); a confirmed hard stop freezes + closes. Either terminates
          // the bundle — no further answers post past a stop/pending stop.
          if (applyStaffResult(question, value, detail, result)) return;
          // R28 gate: an unseen nextQuestionKey means the server just unlocked
          // restricted questions — refetch and merge them into the page.
          if (typeof result.nextQuestionKey === 'string' && !questions.some((qq) => qq.key === result.nextQuestionKey)) {
            await refetch();
          }
        }
      })
      .catch(() => {
        failedKeysRef.current.add(question.key);
        setSaveStates((prev) => ({ ...prev, [question.key]: 'error' }));
        setSaveErrorKey(question.key);
      });
  }

  /** Re-queue the last attempted payload for a failed field (same value, same detail). */
  function retry(question: QuestionPublic) {
    const attempt = lastAttemptRef.current[question.key];
    if (attempt) save(question, attempt.value, attempt.detail);
  }

  /**
   * Continue — COMMIT the previewed hard-stop close: re-post the same answer with
   * `confirmClose: true`. The pending-close dialog stays up (freezing the form)
   * until the queued post resolves; the returned `hard_stop` then freezes/closes
   * for good. Kept set through the await so the form never briefly unlocks.
   */
  async function confirmPendingClose() {
    if (!pendingClose || confirmingClose) return;
    const { question, value, detail } = pendingClose;
    setConfirmingClose(true);
    save(question, value, detail, true);
    try {
      await queueRef.current;
    } catch {
      // save() owns its own failure surface (saveErrorKey); nothing to add here.
    }
    setPendingClose(null);
    setConfirmingClose(false);
  }

  async function handleSubmit() {
    if (frozen || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await queueRef.current; // every field save lands first
      // A save that failed DURING that flush must still block the submit —
      // the engine holds fewer answers than the page shows.
      if (failedKeysRef.current.size > 0) {
        setSubmitError('Some answers did not save — retry them before checking eligibility.');
        return;
      }
      // Wave 9 §4 — the AUTHED staff submit (…/submit/staff): the verified
      // CLINICIAN role is what attributes the assessment_submitted audit row.
      const result = await submitStaffAssessment(assessmentId);
      if (result.eligibility === 'not_eligible') {
        // CLIN-02 holds here too — grey out + popup, never a positive verdict.
        setNotEligible(true);
        setStopPopup(true);
        onStopped?.(null);
        return;
      }
      onSubmitted(result);
    } catch {
      setSubmitError('Sorry — we could not check eligibility. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loadState === 'loading') return <DrawerSkeleton blocks={3} label="Loading the questionnaire…" />;
  if (loadState === 'error') return <ErrorState message="Sorry — we could not load the questionnaire." onRetry={load} />;

  // TA-06 — a failed answer POST gets the same centred ErrorState as every
  // other API failure. Try again re-queues the exact payload (radios cannot
  // be re-picked to the same value). The form is replaced until retry succeeds.
  if (saveErrorKey) {
    const failedQuestion = questions.find((q) => q.key === saveErrorKey);
    return (
      <ErrorState
        message="Sorry — we could not save that answer. Please try again."
        onRetry={() => {
          if (failedQuestion) retry(failedQuestion);
          else setSaveErrorKey(null);
        }}
      />
    );
  }

  // Wave 9 §2 — height/weight is captured ONCE: only the FIRST capture
  // renders; the duplicate confirm capture(s) are suppressed (auto-answered —
  // see load()/save()).
  const primaryCaptureKey = ordered(questions).find((q) => q.kind === 'capture')?.key ?? null;
  const visible = ordered(questions)
    .filter((q) => isVisible(q, answers))
    .filter((q) => q.kind !== 'capture' || q.key === primaryCaptureKey);
  const answerable = visible.filter((q) => q.kind !== 'capture');
  // A key whose save FAILED does not count as answered — the optimistic value
  // is local-only, so submitting would post an incomplete assessment.
  const failed = answerable.filter((q) => saveStates[q.key] === 'error');
  const complete =
    answerable.length > 0 && failed.length === 0 && answerable.every((q) => answers[q.key] !== undefined);
  const stopped = stop !== null || notEligible;
  // Progress over the CURRENTLY-VISIBLE questions. A key whose save failed
  // does not count as answered (mirrors the Submit gate above — the server
  // never recorded it), so the bar and the N-of-M chip stay honest.
  const answered = answerable.filter((q) => answers[q.key] !== undefined && saveStates[q.key] !== 'error').length;

  // Wave 9 §2 — the live insight panel's inputs. The capture value drives the
  // projection chart; the BMI dial additionally waits for sex + date of birth
  // (mirrors the journey: BMI is shown with the demographics that qualify it —
  // client compute only, the server's BMI stays authoritative). The sex
  // question is found by its option vocabulary (config-driven — never by key),
  // and a failed save does not count (same honesty rule as the progress bar).
  const captureValue = primaryCaptureKey ? captureOf(answers[primaryCaptureKey]) : null;
  const dobAnswered = questions.some(
    (q) => q.kind === 'date' && answers[q.key] !== undefined && saveStates[q.key] !== 'error',
  );
  const sexAnswered = questions.some(
    (q) =>
      q.kind === 'single_select' &&
      (q.options ?? []).some((o) => o.key === 'male') &&
      answers[q.key] !== undefined &&
      saveStates[q.key] !== 'error',
  );
  const bmi = captureValue && dobAnswered && sexAnswered ? bmiOf(captureValue.heightCm, captureValue.weightKg) : null;

  return (
    <div>
      {answerable.length > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)', marginBottom: 'var(--gps-s-3)' }}>
          <ProgressRail percent={(answered / answerable.length) * 100} label="Questionnaire progress" />
          <Badge tone={answered === answerable.length ? 'positive' : 'muted'}>
            {answered} of {answerable.length} answered
          </Badge>
        </div>
      ) : null}

      {/* FILL-ON-BEHALF: YOU ARE THE SAFETY GATE ON THIS CALL (CTO, 2026-08-19:
          "when clinicians filling on behalf, for them to make sure it's not
          missed, show it in their screen saying they need to explain this").

          WHY IT IS HERE AND NOT ON THE SUBMIT: a patient filling the journey
          themselves meets the medication-safety gate at checkout and cannot pay
          until they tick it. Someone whose form is being filled FOR them never
          sees that screen — the staff member is the only person in the loop who
          can cover the risks, and they can only do it while the call is still
          live. A prompt attached to the submit would arrive after the
          conversation it is asking for.

          Gated on a LIVE form: `readOnly` is an admin (or a later reader)
          looking at a submitted form with no call in progress, and `stopped` is
          a person who is not eligible for treatment at all. Asking either of
          them to explain a medicine's risks is noise, and noise on this band is
          how the band stops being read.

          `role="status"`, not `alert`: this screen's assertive region is the
          hard-stop banner below, and two of them fight each other in a screen
          reader. */}
      {!readOnly && !stopped ? (
        <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
            You must explain this treatment&rsquo;s risks on this call
          </strong>
          <p style={{ margin: 0 }}>
            This person is not filling the form themselves, so nothing has shown them what the treatment can do to
            them, what to watch for, or when to stop and get urgent medical help. Talk them through it before they
            commit to a plan, and record that you did.
          </p>
          <p style={{ margin: 'var(--gps-s-2) 0 0' }}>
            The clinician who approves the prescription can see whether anyone did &mdash; an order with nothing
            recorded is one they have to cover themselves before they can sign it.
          </p>
        </Alert>
      ) : null}

      {softKeys.length > 0 && !stopped ? (
        <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          Noted — a clinician will review the flagged answer{softKeys.length === 1 ? '' : 's'}. You can carry on.
        </Alert>
      ) : null}

      {stopped ? (
        /* ⚠️ THIS ONE DELIBERATELY DOES **NOT** USE THE SHARED
         * `NotEligibleBanner`, AND THAT IS NOT AN OVERSIGHT — I tried it and
         * reverted it (2026-09-06). Read this before "fixing" it the same way.
         *
         * The drawer's three tabs were moved onto that component because they
         * were asserting a stage they never read. This banner is the opposite
         * case: the stop is happening RIGHT NOW, in this session, so the card
         * in memory still holds the PRE-stop stage. Feeding `deal.stage` in
         * here makes the banner announce "still OPEN in New lead" at the exact
         * moment the server closed the deal — newly wrong in the common case,
         * which is the whole reason this stayed as it is.
         *
         * Getting it right would mean re-deriving `isAutoMovable` /
         * `AUTOMATION_HELD_STAGES` on the client, i.e. a second copy of a
         * server rule that would drift the first time the server's list
         * changes. Not worth it, because the held-stage case is not reachable
         * from here: the live form only mounts when `editableForm` is true
         * (`role === 'CLINICIAN' && !submitted && !notEligible`), so an
         * already-not-eligible deal recovered into an admin lane renders the
         * READ-ONLY summary in QuestionnairePanel — which does read the real
         * stage — and never this form. Every stage that can actually reach
         * this line is auto-movable, so "moved to Lost" is true when it shows.
         *
         * role="alert", not "status": this is the one surface where the stop
         * interrupts someone mid-session rather than greeting them on arrival. */
        <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
          This person is not eligible — the deal has moved to Lost. Advise the caller; nothing further can be
          submitted on this form.
        </Alert>
      ) : null}

      {/* Wave 9 §2 — the form column with the live insight panel ALONGSIDE
          (projection + BMI); the panel wraps under the form on narrow drawers
          and is hidden on a stopped form (no weight-loss projection for a
          person who is not eligible). */}
      <div style={{ display: 'flex', gap: 'var(--gps-s-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* The one page: every visible question, greyed once stopped/read-only.
            The fieldset itself is NOT `disabled` and no longer carries the block
            opacity (Wave 4): every question control is individually gated on
            `frozen` already, and a disabled/greyed fieldset would also freeze and
            fade the per-answer NOTE affordance (`renderQuestionExtra`) that must
            stay fully usable on a read-only submitted form — QuestionField greys
            its own prompt+control instead. */}
        <fieldset
          aria-label="Questionnaire"
          style={{ border: 0, margin: 0, padding: 0, display: 'grid', gap: 'var(--gps-s-4)', flex: '1 1 380px', minWidth: 0 }}
        >
          {visible.map((question) => (
            <QuestionField
              key={question.key}
              question={question}
              value={answers[question.key]}
              prefillDetail={prefillDetails[question.key]}
              saveState={saveStates[question.key]}
              soft={softKeys.includes(question.key)}
              disabled={frozen}
              assessmentId={assessmentId}
              clinicSlug={clinicSlug}
              extra={renderQuestionExtra?.(question)}
              onSave={(value, detail) => save(question, value, detail)}
              onRetry={() => retry(question)}
            />
          ))}
        </fieldset>

        {captureValue && !stopped ? (
          <aside
            aria-label="Projected results"
            style={{
              flex: '1 1 300px',
              maxWidth: '440px',
              border: '1px solid var(--gps-border)',
              borderRadius: 'var(--gps-r-md)',
              padding: 'var(--gps-s-3)',
              background: 'var(--gps-surface-2)',
            }}
          >
            {/* The journey's projection component, reused verbatim — R28-safe
                (category-level copy, mandatory disclaimer inside). */}
            <ProjectionChart weightKg={captureValue.weightKg} />
            {bmi !== null ? (
              <BmiDial bmi={bmi} />
            ) : (
              <Help style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>
                The BMI dial appears once height, weight, sex at birth and date of birth are all
                recorded.
              </Help>
            )}
          </aside>
        ) : null}
      </div>

      {submitError ? (
        <Alert tone="danger" role="alert" style={{ margin: 'var(--gps-s-3) 0' }}>
          {submitError}
        </Alert>
      ) : null}

      {!readOnly && !stopped ? (
        <div style={{ marginTop: 'var(--gps-s-4)' }}>
          {/* Wave 9 §3 — the submit IS the eligibility check: name it that. */}
          <Button
            type="button"
            variant="cta"
            disabled={!complete || submitting}
            onClick={() => void handleSubmit()}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          >
            {submitting ? 'Checking eligibility…' : 'Check eligibility'}
          </Button>
          {failed.length > 0 ? (
            <Help style={{ marginTop: 'var(--gps-s-2)' }}>
              These answers did not save: {failed.map((q) => q.prompt).join(' · ')} — retry them before checking eligibility.
            </Help>
          ) : !complete ? (
            <Help style={{ marginTop: 'var(--gps-s-2)' }}>Every question needs an answer before checking eligibility.</Help>
          ) : null}
        </div>
      ) : null}

      {/* CONFIRM-BEFORE-LOST — a mis-tapped disqualifying answer no longer
          silently loses the lead. The lead is NOT lost until "Continue". */}
      {pendingClose ? (
        <DialogShell title="Close the assessment and mark the lead as Lost?" onCancel={() => setPendingClose(null)}>
          <p style={{ marginTop: 0, color: 'var(--gps-text)', fontSize: '14.5px' }}>
            Selecting this answer will close the assessment and mark the lead as Lost. Continue?
          </p>
          <div style={{ display: 'flex', gap: '8px', marginTop: 'var(--gps-s-2)' }}>
            <Button
              type="button"
              variant="cta"
              disabled={confirmingClose}
              onClick={() => void confirmPendingClose()}
              style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', background: 'var(--gps-danger-strong)' }}
            >
              {confirmingClose ? 'Closing…' : 'Continue'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={confirmingClose}
              onClick={() => setPendingClose(null)}
              style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
            >
              Go back
            </Button>
          </div>
        </DialogShell>
      ) : null}

      {stopPopup ? (
        <DialogShell title="This person is not eligible" onCancel={() => setStopPopup(false)}>
          <p style={{ marginTop: 0, color: 'var(--gps-text)', fontSize: '14.5px' }}>
            The answers rule this treatment out. Let the caller know, and share the guidance below where it applies.
          </p>
          {(stop?.signposting?.lines ?? []).map((line) => (
            <p key={line} style={{ color: 'var(--gps-text)', fontSize: '14.5px' }}>
              {line}
            </p>
          ))}
          <Button type="button" variant="primary" onClick={() => setStopPopup(false)} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}>
            Understood
          </Button>
        </DialogShell>
      ) : null}
    </div>
  );
}

/** One compact field, by kind — posts on change/blur via the parent queue. */
function QuestionField({
  question,
  value,
  prefillDetail,
  saveState,
  soft,
  disabled,
  assessmentId,
  clinicSlug,
  extra,
  onSave,
  onRetry,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  prefillDetail: string | undefined;
  saveState: SaveState | undefined;
  soft: boolean;
  disabled: boolean;
  assessmentId: string;
  clinicSlug: string;
  /** The caller's per-question affordance (Wave 4 anchored notes), if any. */
  extra?: ReactNode;
  onSave: (value: AnswerValue, detail?: string) => void;
  /** Re-queue the failed payload verbatim — the only working retry for a radio. */
  onRetry: () => void;
}) {
  const status =
    saveState === 'saving' ? (
      <span style={chipStyle('muted')}>Saving…</span>
    ) : saveState === 'saved' ? (
      <span style={{ ...chipStyle('muted'), display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        Saved <CheckIcon size={12} />
      </span>
    ) : saveState === 'error' ? (
      <span role="alert" style={chipStyle('danger')}>
        Did not save
        <Button
          type="button"
          variant="ghost"
          disabled={disabled}
          onClick={onRetry}
          style={{ width: 'auto', minHeight: '28px', padding: '2px 10px', fontSize: '12px', marginLeft: '8px' }}
        >
          Retry
        </Button>
      </span>
    ) : null;

  return (
    <div>
      {/* The prompt + control grey together when frozen; the `extra` (Wave 4
          anchored-note affordance) sits OUTSIDE the fade at full opacity. */}
      <div style={{ opacity: disabled ? 0.55 : 1 }}>
        <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '15px', color: 'var(--gps-text)' }}>
          {question.prompt}
          {status}
        </p>
        {question.helpText ? <Help style={{ marginTop: 0 }}>{question.helpText}</Help> : null}
        {soft ? (
          <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--gps-warn-text)' }}>
            Flagged for clinician review — carry on.
          </p>
        ) : null}
        <FieldControl question={question} value={value} prefillDetail={prefillDetail} disabled={disabled} assessmentId={assessmentId} clinicSlug={clinicSlug} onSave={onSave} />
      </div>
      {extra ?? null}
    </div>
  );
}

function FieldControl({
  question,
  value,
  prefillDetail,
  disabled,
  assessmentId,
  clinicSlug,
  onSave,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  prefillDetail: string | undefined;
  disabled: boolean;
  assessmentId: string;
  clinicSlug: string;
  onSave: (value: AnswerValue, detail?: string) => void;
}) {
  switch (question.kind) {
    case 'capture':
      return <CaptureField question={question} value={value} disabled={disabled} onSave={onSave} />;
    case 'date':
      return <DateField question={question} value={value} disabled={disabled} onSave={onSave} />;
    case 'single_select':
      return <RadioField question={question} value={value} disabled={disabled} onSave={onSave} />;
    case 'multi_select':
      return <MultiField question={question} value={value} disabled={disabled} onSave={onSave} />;
    case 'yes_no_text':
      return <YesNoField question={question} value={value} prefillDetail={prefillDetail} disabled={disabled} onSave={onSave} />;
    case 'photo':
      return <PhotoField question={question} value={value} disabled={disabled} assessmentId={assessmentId} clinicSlug={clinicSlug} onSave={onSave} />;
    case 'consent':
      return <ConsentField question={question} value={value} disabled={disabled} onSave={onSave} />;
    default:
      return null;
  }
}

/**
 * A WHOLE ISO `yyyy-mm-dd` date — the only shape the answer API takes, gated
 * here exactly as the admin audit filter bar gates its own date fields.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * date — the themed DatePicker, capped at today (the server owns the real age
 * rule; this only stops a future date being picked).
 *
 * WHY the half-typed date lives HERE and not in `answers`: DatePicker is a
 * CONTROLLED field, so it hands its caller EVERY keystroke — the partial
 * prefixes on the way to a date ('1', '19', '1990-0') as much as the finished
 * one — and leaves it to the caller to decide when a partial is worth acting
 * on. The patient journey holds them behind a Continue button; this page has
 * no Continue, it autosaves, and it wired the field straight to the save. So
 * every prefix was POSTed as an answer, each one 400d, and on this form a
 * failed save REPLACES the whole questionnaire with "we could not save that
 * answer" — a date of birth typed on a keypad is eight keystrokes, so the
 * clinician watched the form vanish mid-word SEVEN times on a live call for
 * the one field. (The optimistic write also briefly fed the BMI dial a
 * two-digit year.) Keeping the in-progress text local keeps the field
 * typeable while only a whole date ever reaches the queue.
 *
 * An emptied field saves NOTHING, as before: the engine has no "unanswer"
 * write, so the last saved date stands until a whole new one replaces it.
 */
function DateField({
  question,
  value,
  disabled,
  onSave,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  disabled: boolean;
  onSave: (value: AnswerValue) => void;
}) {
  // Seeded from the answer the form mounted with (prefill on a resumed form,
  // the server's raw answers when read-only); a date answer only ever changes
  // from this field, so there is nothing to sync back afterwards.
  const [typed, setTyped] = useState(typeof value === 'string' ? value : '');

  return (
    <div style={{ maxWidth: '260px' }}>
      <DatePicker
        id={`spf-${question.key}`}
        value={typed}
        max={todayIso()}
        // Same server question the journey renders — the patient's date of
        // birth, read out over the phone — so the empty calendar opens ~18
        // years back here too rather than in the current year. View only:
        // nothing is written into the field, and `max` stays today.
        openView="dob"
        disabled={disabled}
        onChange={(next) => {
          setTyped(next);
          if (ISO_DATE.test(next)) onSave(next);
        }}
      />
    </div>
  );
}

/** Height/weight in one row — the BMI inputs, editable mid-call. */
function CaptureField({
  question,
  value,
  disabled,
  onSave,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  disabled: boolean;
  onSave: (value: AnswerValue) => void;
}) {
  const current = value && typeof value === 'object' && !Array.isArray(value) ? value : { heightCm: 0, weightKg: 0 };
  const [heightCm, setHeightCm] = useState(current.heightCm ? String(current.heightCm) : '');
  const [weightKg, setWeightKg] = useState(current.weightKg ? String(current.weightKg) : '');

  function commit() {
    const h = Number(heightCm);
    const w = Number(weightKg);
    if (!Number.isFinite(h) || !Number.isFinite(w) || h <= 0 || w <= 0) return;
    if (h === current.heightCm && w === current.weightKg) return;
    onSave({ heightCm: h, weightKg: w });
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--gps-s-3)', maxWidth: '420px' }}>
      <Field label="Height (cm)" htmlFor={`spf-${question.key}-h`}>
        <TextInput id={`spf-${question.key}-h`} inputMode="decimal" value={heightCm} disabled={disabled} onChange={(e) => setHeightCm(e.target.value)} onBlur={commit} />
      </Field>
      <Field label="Weight (kg)" htmlFor={`spf-${question.key}-w`}>
        <TextInput id={`spf-${question.key}-w`} inputMode="decimal" value={weightKg} disabled={disabled} onChange={(e) => setWeightKg(e.target.value)} onBlur={commit} />
      </Field>
    </div>
  );
}

function RadioField({
  question,
  value,
  disabled,
  onSave,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  disabled: boolean;
  onSave: (value: AnswerValue) => void;
}) {
  return (
    <div role="radiogroup" aria-label={question.prompt} style={{ display: 'grid', gap: '4px' }}>
      {(question.options ?? []).map((option) => (
        <label key={option.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14.5px', color: 'var(--gps-text)', minHeight: '32px', cursor: disabled ? 'default' : 'pointer' }}>
          <input
            type="radio"
            name={`spf-${question.key}`}
            checked={value === option.key}
            disabled={disabled}
            onChange={() => onSave(option.key)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}

/** multi_select — the exclusive "none" option clears the rest (and vice versa); each change saves. */
function MultiField({
  question,
  value,
  disabled,
  onSave,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  disabled: boolean;
  onSave: (value: AnswerValue) => void;
}) {
  const picked = Array.isArray(value) ? value : [];
  const options = question.options ?? [];
  // The engine rejects an EMPTY multi_select, so unticking the last option is
  // refused — without feedback the checkbox just looks stuck (the click is a
  // silent no-op). Explain instead of silently ignoring.
  const [emptyRefused, setEmptyRefused] = useState(false);
  const exclusiveLabel = options.find((o) => o.exclusive)?.label;

  function toggle(key: string) {
    const option = options.find((o) => o.key === key);
    let next: string[];
    if (option?.exclusive) {
      next = picked.includes(key) ? [] : [key];
    } else {
      const cleared = picked.filter((k) => !options.find((o) => o.key === k)?.exclusive);
      next = cleared.includes(key) ? cleared.filter((k) => k !== key) : [...cleared, key];
    }
    if (next.length > 0) {
      setEmptyRefused(false);
      onSave(next);
    } else {
      setEmptyRefused(true);
    }
  }

  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      {options.map((option) => (
        <CheckboxRow
          key={option.key}
          label={option.label}
          checked={picked.includes(option.key)}
          disabled={disabled}
          onChange={() => toggle(option.key)}
        />
      ))}
      {emptyRefused ? (
        <p role="status" style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--gps-warn-text)' }}>
          The form needs one answer here — {exclusiveLabel ? `pick another option or “${exclusiveLabel}”` : 'pick another option'} to change it.
        </p>
      ) : null}
    </div>
  );
}

/** yes_no_text — "No" saves straight away; "Yes" waits for the detail's blur. */
function YesNoField({
  question,
  value,
  prefillDetail,
  disabled,
  onSave,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  /** The previously saved free text for a resumed/read-only "yes" (GET …/answers). */
  prefillDetail: string | undefined;
  disabled: boolean;
  onSave: (value: AnswerValue, detail?: string) => void;
}) {
  const [choice, setChoice] = useState<string | null>(typeof value === 'string' ? value : null);
  const [detail, setDetail] = useState(prefillDetail ?? '');
  // WHY the dirty flag: a resumed "yes" seeds its saved detail above, but the
  // stored text may be richer than what this session sees — blurring an
  // UNTOUCHED box must never re-post (it would overwrite the clinical detail
  // captured earlier in the call with whatever this render happens to hold).
  const [dirty, setDirty] = useState(false);

  return (
    <div>
      <div role="radiogroup" aria-label={question.prompt} style={{ display: 'flex', gap: 'var(--gps-s-3)' }}>
        {(['no', 'yes'] as const).map((key) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14.5px', color: 'var(--gps-text)', minHeight: '32px', cursor: disabled ? 'default' : 'pointer' }}>
            <input
              type="radio"
              name={`spf-${question.key}`}
              checked={choice === key}
              disabled={disabled}
              onChange={() => {
                setChoice(key);
                setDirty(true);
                if (key === 'no') onSave('no');
              }}
            />
            {key === 'no' ? 'No' : 'Yes'}
          </label>
        ))}
      </div>
      {choice === 'yes' ? (
        <Field label="Details" htmlFor={`spf-${question.key}-detail`}>
          <TextArea
            id={`spf-${question.key}-detail`}
            value={detail}
            disabled={disabled}
            onChange={(e) => {
              setDetail(e.target.value);
              setDirty(true);
            }}
            onBlur={() => {
              if (dirty) onSave('yes', detail.trim() || undefined);
            }}
          />
        </Field>
      ) : null}
    </div>
  );
}

/** photo — upload via the signed grant, or defer to dispatch (never blocking). */
function PhotoField({
  question,
  value,
  disabled,
  assessmentId,
  clinicSlug,
  onSave,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  disabled: boolean;
  assessmentId: string;
  clinicSlug: string;
  onSave: (value: AnswerValue) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = question.options ?? [];
  const deferKey = options.find((o) => /later|dispatch|skip/i.test(o.key))?.key ?? 'add_later';
  const uploadKey = options.find((o) => /now|upload/i.test(o.key))?.key ?? 'upload_now';

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const grant = await grantPhotoUpload(assessmentId, clinicSlug);
      await uploadPhoto(grant, file);
      onSave(uploadKey);
    } catch {
      setError('Sorry — the upload did not work. Defer it instead; it never blocks the review.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--gps-s-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        ref={fileInputRef}
        data-testid={`spf-photo-${question.key}`}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
      <Button type="button" variant="ghost" disabled={disabled || uploading} onClick={() => fileInputRef.current?.click()} style={{ width: 'auto', minHeight: '40px', padding: '8px 14px' }}>
        {uploading ? 'Uploading…' : 'Upload a photo…'}
      </Button>
      <Button type="button" variant="ghost" disabled={disabled || uploading} onClick={() => onSave(deferKey)} style={{ width: 'auto', minHeight: '40px', padding: '8px 14px' }}>
        The patient will add it before their order is dispatched
      </Button>
      {value === deferKey ? <Help style={{ margin: 0 }}>Deferred to dispatch.</Help> : null}
      {value === uploadKey ? <Help style={{ margin: 0 }}>Photo uploaded.</Help> : null}
      {error ? (
        <p role="alert" style={{ margin: 0, fontSize: '13px', color: 'var(--gps-danger-text)' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** consent — the answer posts once EVERY box is ticked (a consent gate, not eligibility). */
function ConsentField({
  question,
  value,
  disabled,
  onSave,
}: {
  question: QuestionPublic;
  value: AnswerValue | undefined;
  disabled: boolean;
  onSave: (value: AnswerValue) => void;
}) {
  const options = question.options ?? [];
  const saved = Array.isArray(value) ? value : [];
  const [ticked, setTicked] = useState<string[]>(saved);

  function toggle(key: string) {
    const next = ticked.includes(key) ? ticked.filter((k) => k !== key) : [...ticked, key];
    setTicked(next);
    if (options.length > 0 && next.length === options.length) onSave(options.map((o) => o.key));
  }

  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      {options.map((option) => (
        <CheckboxRow
          key={option.key}
          label={option.label}
          checked={ticked.includes(option.key)}
          disabled={disabled}
          onChange={() => toggle(option.key)}
        />
      ))}
    </div>
  );
}
